import React, { useState } from "react";
import { LogIn, Eye, EyeOff, AlertCircle } from "lucide-react";
import { API_BASE_URL } from "../utils/apiConfig";

interface LoginProps {
  onLogin: (token: string, user: any) => void;
  loading?: boolean;
}

const Login: React.FC<LoginProps> = ({ onLogin, loading = false }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check for remembered credentials on component mount
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberMePref = localStorage.getItem("rememberMe");

    if (rememberedEmail && rememberMePref === "true") {
      setFormData((prev) => ({
        ...prev,
        email: rememberedEmail,
      }));
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for cross-origin cookies
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        const token = data.data.token;
        localStorage.setItem("authToken", token);
        if (data.data.refreshToken) {
          localStorage.setItem("refreshToken", data.data.refreshToken);
        }

        // Load full user (permissions, profile, etc.) from /me so call-center abilities are available without refresh
        const meResponse = await fetch(`${API_BASE_URL}/api/auth/me`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const meData = meResponse.ok ? await meResponse.json() : null;
        const user = meData?.success && meData?.data?.user
          ? meData.data.user
          : data.data.user;
        localStorage.setItem("user", JSON.stringify(user));

        // Store remember me preference and extended session
        const currentTime = Date.now();
        if (rememberMe) {
          const expirationTime = currentTime + 30 * 24 * 60 * 60 * 1000; // 30 days
          localStorage.setItem("rememberMe", "true");
          localStorage.setItem("rememberedEmail", formData.email);
          localStorage.setItem("loginTime", currentTime.toString());
          localStorage.setItem("sessionExpiration", expirationTime.toString());

          console.log("✅ [LOGIN] Remember Me enabled - 30 day session");
          console.log(
            "📅 [LOGIN] Session expires:",
            new Date(expirationTime).toLocaleString()
          );
        } else {
          const expirationTime = currentTime + 24 * 60 * 60 * 1000; // 24 hours
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("rememberedEmail");
          localStorage.setItem("sessionExpiration", expirationTime.toString());

          console.log("⏰ [LOGIN] Standard session - 24 hours");
          console.log(
            "📅 [LOGIN] Session expires:",
            new Date(expirationTime).toLocaleString()
          );
        }

        onLogin(token, user);
      } else {
        setError(data.error || "Login failed");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex flex-col items-center space-y-2">
            <img
              src="/logo_without_text.svg"
              alt="Natura Therapy"
              className="w-16 h-16"
            />
            <h2 className="text-3xl font-bold text-gray-900">Natura Therapy</h2>
          </div>
        </div>
        <p className="mt-2 text-center text-sm text-gray-600">
          Order Tracking & Management System
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email or Username
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-[#264a3e] focus:border-[#264a3e] sm:text-sm"
                  placeholder="Enter your email or username"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-[#264a3e] focus:border-[#264a3e] sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-[#264a3e] focus:ring-[#264a3e] border-gray-300 rounded"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Remember me for 30 days
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || loading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#264a3e] hover:bg-[#1e3a30] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading || loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
